<template>
  <RevForm
    class="flex items-start gap-8"
    :initial-values
    name="'mailbook-contact'"
    :validate
    @submit="handleSubmit"
  >
    <template #default="{ values, errors, hasError }">
      <div class="flex w-full flex-col gap-8 md:flex-row">
        <RevInputText
          id="mailbook-email"
          v-model="values.email"
          autocomplete="email"
          class="w-full md:w-[300px]"
          data-cs-mask
          :disabled="isSubmitting"
          :error="errors.email"
          :icon="IconMail"
          :label="i18n(translations.emailLabel)"
          :placeholder="i18n(translations.emailPlaceholder)"
          type="email"
        />
        <RevInputSelect
          id="mailbook-contact-type"
          v-model="values.type"
          class="w-full md:w-[300px]"
          :label="i18n(translations.contactTypeLabel)"
          :options="contactTypes"
          :placeholder="i18n(translations.contactTypePlaceholder)"
        />
        <RevButton
          id="add-contact-button"
          :disabled="hasError || isSubmitting"
          :loading="isSubmitting"
          type="submit"
          variant="primary"
        >
          {{ i18n(translations.addContactButton) }}
        </RevButton>
      </div>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import {
  RevForm,
  makeValidate,
  matchingRegExp,
  required,
} from '@ds/components/Form'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { IconMail } from '@ds/icons/IconMail'

import { REGEXP_VALID_EMAIL } from '~/constants/regex'

import translations from '../Mailbook.translations'
import type { FormContactValues } from '../Mailbook.types'

const $httpFetch = useHttpFetch()

const emit = defineEmits<{
  (e: 'contact-added'): void
}>()

defineProps<{
  contactTypes: {
    value: string
    label: string
  }[]
}>()

const i18n = useI18n()
const { openSuccessToast, openErrorToast } = useTheToast()
const isSubmitting = ref(false)
const initialValues = { email: '', type: '' } as FormContactValues

const validate = makeValidate<FormContactValues>({
  email: [
    required(i18n(translations.emailRequired)),
    matchingRegExp(REGEXP_VALID_EMAIL, i18n(translations.emailInvalid)),
  ],
  type: [required(i18n(translations.fieldIsRequired))],
})

async function handleSubmit({ email, type }: FormContactValues) {
  isSubmitting.value = true

  try {
    await $httpFetch('/api/seller-experience/mailbook', {
      method: 'POST',
      body: {
        emailAddress: email,
        contactType: type,
      },
    })

    emit('contact-added')
    openSuccessToast({ title: i18n(translations.toastAddSuccessContent) })
  } catch {
    openErrorToast({ title: i18n(translations.toastErrorContent) })
  } finally {
    isSubmitting.value = false
  }
}
</script>
